import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { Input } from "@src/components/input/Input";
import { Switch } from "@src/components/switch/Switch";
import { pricingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { IListingToUpdate } from "@src/store/pricing/updateFlashSale";
import { FlashSaleValidation, FlashSaleValidationType } from "@src/validation/FlashSaleValidation";
import React, { useEffect, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { IAdvancedTab } from "../../AdvancedTab";
import { SubHeader } from "../SubHeader/SubHeader";
import styles from "./FlashSale.module.scss";
import { HighlightedSectionEnum } from "@src/store/pricing/selectionSlice";
import useHighlightedSection from "@src/hooks/useHighlightedSection";
import useScrollToBottom from "@src/hooks/useScrollToBottom";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";

export const FlashSale: React.FC<IAdvancedTab> = ({ inventoryItem, onSuccess }) => {
	const flashSaleLoading = useAppSelector((state) => state.pricing.flashSale.loading);
	const disableflashSaleLoading = useAppSelector((state) => state.pricing.disableFlashSale.loading);
	const dispatch = useAppDispatch();

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		reset,
		formState: { isDirty: isFsDirty },
	} = useForm({
		resolver: yupResolver(FlashSaleValidation),
		mode: "onChange",
		defaultValues: {
			flashSaleEnable: inventoryItem?.FlashSaleEnabled ?? false,
			lastMinuteDiscount: inventoryItem?.FlashSaleDiscount ?? 0,
		},
	});

	useEffect(() => {
		reset({
			flashSaleEnable: inventoryItem?.FlashSaleEnabled ?? false,
			lastMinuteDiscount: inventoryItem?.FlashSaleDiscount ?? 0,
		});
	}, [inventoryItem]);

	const flashSaleEnable = watch("flashSaleEnable");

	const onFsReset = () => {
		reset();
	};

	const onEnableSwitch = () => {
		setValue("flashSaleEnable", !flashSaleEnable, { shouldDirty: true });
	};

	const onFlashSaleSubmit: SubmitHandler<FlashSaleValidationType> = async (data) => {
		const listingID = inventoryItem?.Listing_ID;
		if (!data.lastMinuteDiscount || !listingID) return;
		let payload = { listingIds: [listingID] } as IListingToUpdate;
		if (data.flashSaleEnable) payload = { ...payload, discount: String(data.lastMinuteDiscount) };

		mixpanelTrack(MixpanelCategory.SET_FLASH_SALE);

		try {
			data.flashSaleEnable
				? await dispatch(pricingActions.updateFlashSale(payload)).unwrap()
				: await dispatch(pricingActions.disableFlashSale([listingID])).unwrap();
			toast.success(`Last Minute Discount ${data.flashSaleEnable ? "Enabled" : "Disabled"} successfully`);
			onSuccess?.();
		} catch (error) {
			toast.warning("Error while processing your request");
		}
	};

	const isHighlighted = useHighlightedSection(HighlightedSectionEnum.FlashSale);
	const divRef = useRef<HTMLDivElement>(null);
	useScrollToBottom(divRef, 300, isHighlighted);

	return (
		<div ref={divRef} className={styles.flashSaleMain}>
			<SubHeader isHighlighted={isHighlighted} title="Flash Sale" tooltipText="Flash Sale" />
			<form className={styles.flashSaleForm} onSubmit={handleSubmit(onFlashSaleSubmit)}>
				<div className={styles.flashSaleInput}>
					<div style={{ width: "165px" }}>
						<Input
							disabled={!flashSaleEnable}
							style={{ width: "135px" }}
							controlSize="sm"
							label="Last Minute Discount (%)"
							type="number"
							control={control}
							name="lastMinuteDiscount"
							step={1}
						/>
					</div>
					<div className={styles.switchContainer}>
						<Switch
							checked={!!flashSaleEnable}
							onChange={onEnableSwitch}
							label="Enable"
							tooltip="Toggle to enable input"
							tooltipId="flashSaleTooltip"
						/>
					</div>
				</div>
				<div className={styles.buttons}>
					<Button
						type="submit"
						controlSize="sm"
						variant={ButtonVariant.Secondary}
						style={{ padding: "0px 12px" }}
						disabled={!isFsDirty}
						onClick={onFsReset}>
						Reset
					</Button>
					<Button
						isLoading={flashSaleLoading || disableflashSaleLoading}
						type="submit"
						variant={ButtonVariant.Primary}
						style={{ minWidth: "123.86px" }}
						controlSize="sm"
						disabled={!isFsDirty}>
						Set Flash Sale
					</Button>
				</div>
			</form>
		</div>
	);
};
